import { BRACKETS, STANDARD_DEDUCTION, bracketTax, employeeFica, type Filing } from "./usTax";
import { stateTax, statePayrollTax } from "./stateTaxData";

export const PAY_PERIODS: Record<string, number> = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 };

export interface PaycheckInput {
  gross: number;               // annual gross
  filing: Filing;
  state: string;               // state code, or "CUSTOM"
  customStatePct: number;
  retirementPct: number;       // traditional 401(k)/403(b), % of gross: lowers income tax but not FICA
  section125Annual: number;    // health/dental premiums, HSA/FSA: lowers income tax AND FICA
  periods: number;
}
export interface PaycheckResult {
  fed: number; state: number;
  /** State payroll tax withheld from wages on top of state income tax (California SDI); 0 when none is modelled. Same rule as the Income Tax Calculator (B-0278). */
  statePayroll: number; statePayrollLabel: string;
  socialSecurity: number; medicare: number; additionalMedicare: number; fica: number;
  retirement: number; section125: number; netAnnual: number; netPerPeriod: number; grossPerPeriod: number; taxable: number;
}

export function paycheck(i: PaycheckInput): PaycheckResult {
  const gross = Math.max(0, i.gross);
  const retirement = Math.min(gross, (gross * Math.max(0, i.retirementPct)) / 100);
  const s125 = Math.min(gross, Math.max(0, i.section125Annual));
  const std = STANDARD_DEDUCTION[i.filing];
  const taxable = Math.max(0, gross - retirement - s125 - std);
  const fed = bracketTax(taxable, BRACKETS[i.filing]);
  const ficaWages = gross - s125;
  const f = employeeFica(ficaWages, i.filing);
  const stateBase = Math.max(0, gross - retirement - s125);
  const state = i.state === "CUSTOM" ? (stateBase * Math.max(0, i.customStatePct)) / 100 : stateTax(i.state, stateBase, i.filing, std).tax;
  // California SDI: on gross wages (a 401(k) deferral does not lower it), the same base the Income Tax Calculator uses. Not charged for a custom state rate.
  const sdi = i.state === "CUSTOM" ? null : statePayrollTax(i.state, gross);
  const statePayroll = sdi?.amount ?? 0;
  const netAnnual = gross - fed - state - statePayroll - f.total - retirement - s125;
  return {
    fed, state, statePayroll, statePayrollLabel: sdi?.label ?? "", socialSecurity: f.socialSecurity, medicare: f.medicare, additionalMedicare: f.additionalMedicare, fica: f.total,
    retirement, section125: s125, netAnnual, netPerPeriod: netAnnual / i.periods, grossPerPeriod: gross / i.periods, taxable,
  };
}

/** Annual gross from an hourly rate: regular hours up to 40 a week, overtime at 1.5x beyond that. */
export function hourlyToAnnual(rate: number, hoursPerWeek: number, weeks = 52): number {
  const reg = Math.min(hoursPerWeek, 40), ot = Math.max(0, hoursPerWeek - 40);
  return (rate * reg + rate * 1.5 * ot) * weeks;
}
